import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminTitle from '../../../common/AdminTitle/AdminTitle';
import DateFilter from './ui/DateFilter';
import DashboardNav from './ui/DashboardNav';
import OverviewDashboard from './pages/OverviewDashboard';
import SalesAnalyticsView from './pages/SalesAnalyticsView';
import ProductAnalyticsView from './pages/ProductAnalyticsView';
import ExpenseManagementView from './pages/ExpenseManagementView';
import WebsiteAnalyticsView from './pages/WebsiteAnalyticsView';
import { getDateRange } from './dashboardUtils';

/**
 * Main Redesigned Dashboard Component
 * Supports split sub-views, global date filtering, and minimal Zara-style UI
 */
const Dashboard = ({ defaultTab = 'overview' }) => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(defaultTab);

    // Global date filter state
    const [preset, setPreset] = useState('30d');
    const [customRange, setCustomRange] = useState({ from: '', to: '' });

    // Sync tab when prop changes
    useEffect(() => {
        if (defaultTab) {
            setActiveTab(defaultTab);
        }
    }, [defaultTab]);

    // Calculate active date range (from, to) for backend queries
    const activeDateRange =
        preset === 'custom'
            ? customRange
            : getDateRange(preset);

    const handleDateFilterChange = (newPreset, rangeObj) => {
        setPreset(newPreset);
        if (newPreset === 'custom' && rangeObj) {
            setCustomRange({
                from: rangeObj.from || '',
                to: rangeObj.to || '',
            });
        }
    };

    const handleSelectTab = (tabId, path) => {
        setActiveTab(tabId);
        if (path) {
            navigate(path, { replace: true });
        }
    };

    return (
        <div className="w-full min-w-0 space-y-6">
            {/* Top Header & Global Date Filter Bar */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-stone-200/60">
                <div>
                    <AdminTitle />
                    <p className="text-xs text-stone-500 font-medium mt-1">
                        Executive analytics, profit performance & store metrics
                    </p>
                </div>

                {/* Global Date Filter Component */}
                <div className="self-start md:self-auto flex-shrink-0">
                    <DateFilter
                        preset={preset}
                        onChange={handleDateFilterChange}
                        from={customRange.from}
                        to={customRange.to}
                    />
                </div>
            </div>

            {/* Horizontal Sub-Navigation Tab Bar */}
            <DashboardNav activeTab={activeTab} onSelectTab={handleSelectTab} />

            {/* Sub-Page Content Container */}
            <main className="w-full min-w-0 transition-all duration-300">
                {activeTab === 'overview' && (
                    <OverviewDashboard dateRange={activeDateRange} />
                )}
                {activeTab === 'sales' && (
                    <SalesAnalyticsView dateRange={activeDateRange} />
                )}
                {activeTab === 'products' && (
                    <ProductAnalyticsView dateRange={activeDateRange} />
                )}
                {activeTab === 'expenses' && (
                    <ExpenseManagementView dateRange={activeDateRange} />
                )}
                {activeTab === 'website' && (
                    <WebsiteAnalyticsView dateRange={activeDateRange} />
                )}
            </main>
        </div>
    );
};

export default Dashboard;